import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import SearchPanel from "@widgets/SearchPanel";
import SnSearchBtn from "@shared/components/SnSearchBtn";
import SnGrid from "@shared/components/GridV2/SnGrid";
import { SnGridRef } from "@shared/components/GridV2/SnGridTypes";
import { fnGetDataSet } from "@shared/api/restApi";
import { getInitPopUpData } from "@shared/utils/CommonUtils";
import { showDialog } from "@widgets/CommonDialog";

interface popupProps {
  /** 본화면 조회조건(담당자 등) — 팝업 조건 초기값으로 이어받는다. */
  searchData?: any;
  modalRef?: any;
}

/**
 * 이슈가져오기. 체크한 이슈를 전주실적/금주계획 그리드로 가져온다.
 * 조건·컬럼은 메타(cd_menu = SN_SFA_JOURNAL_REG_001_POP_02)에서 온다.
 * 이슈상태는 SN_SFA_ISSUE_REP_001 과 같은 방식으로 yn_open/yn_assign/… 체크박스이고,
 * 코드 매핑(1 Open · 2 Assign · 3 Solving · 4 Done · 5 Confirm · 9 Cancel · T Test)은 쿼리가 한다.
 */
const SN_SFA_JOURNAL_REG_001_POP_02 = forwardRef(
  ({ searchData, modalRef }: popupProps, ref: React.Ref<any>) => {
    const gridRef = useRef<SnGridRef>(null);
    const searchRef = useRef<any>(null);
    const [formData, setFormData] = useState<any>();
    const [formControlData, setFormControlData] = useState<any[]>([]);
    const [columns, setColumns] = useState<any[]>([]);
    const [dataSource, setDataSource] = useState<any[]>([]);

    useImperativeHandle(ref, () => ({
      checkValidity: async () => {
        const checkedRows = gridRef.current?.getCheckedRows?.() ?? [];
        if (!checkedRows.length) {
          await showDialog("alter", "가져올 이슈를 선택하세요.");
          return false;
        }
        return true;
      },
      getOkayData: () => ({
        rtnData: [gridRef.current?.getCheckedRows?.() ?? []],
      }),
    }));

    const onSearch = async (searchDataToUse?: any) => {
      try {
        const params =
          searchDataToUse ?? searchRef.current?.getFormdata?.() ?? {};
        const data: any = await fnGetDataSet({
          id: "sn_sfa_journal_reg_001_pop_02_01",
          params,
        });
        const rows: any[] = data?.[0] ?? [];
        setDataSource(rows);
        if (!rows.length) {
          await showDialog("alter", "조건에 맞는 이슈가 없습니다.");
        }
      } catch (error) {}
    };

    useEffect(() => {
      const fetchForm = async () => {
        try {
          const rtn: any = await getInitPopUpData(
            "SN_SFA_JOURNAL_REG_001_POP_02",
            {}
          );
          // 이슈상태·기간 기본값은 메타(sys_formctrl.cd_value)를 그대로 쓴다.
          // (기간 기본값 = 전월1일~이번달말일은 기간 컨트롤 cd_value '0/-1/F,0/0/L' 에서 처리)
          // 담당자만 본화면 값을 이어받는다 — SnCodeBox 라 코드+이름을 함께 넘겨야 표시된다.
          const merged = {
            ...(rtn.formData ?? {}),
            ...(searchData?.cd_emp
              ? { cd_emp: searchData.cd_emp, nm_emp: searchData.nm_emp ?? "" }
              : {}),
          };
          setFormControlData(rtn.formControlData);
          setFormData(merged);
          setColumns(rtn.grid?.grid1 ?? []);
          await onSearch(merged);
        } catch (error) {}
      };
      fetchForm();
    }, []);

    return (
      <div style={{ height: "55vh" }} className="flex flex-col">
        <div className="mb-1 flex">
          <div className="flex-1 min-w-0">
            <SearchPanel
              ref={searchRef}
              data={formData}
              controls={formControlData}
              popupRef={ref}
              // 상태 체크박스 7개 + '상태' 라벨칸을 한 줄에 넣으려면 팝업 기본 6칸으로는 모자란다.
              gridTemplateColumns={"repeat(8, minmax(0, 1fr))"}
            />
          </div>
          <div className="flex items-center pl-2">
            <SnSearchBtn text={"조회"} onClick={() => onSearch()} />
          </div>
        </div>
        <div className="flex-1 min-h-0">
          <SnGrid
            ref={gridRef}
            columnConfig={columns}
            rowData={dataSource}
            etcProps={{
              rowSelection: "multiple",
              isEditable: false,
              ischeckboxSelection: true,
              popupParent: modalRef?.current?.getPopupParent?.(),
            }}
          />
        </div>
      </div>
    );
  }
);

export default SN_SFA_JOURNAL_REG_001_POP_02;
